import { Currency, Money } from '@principle-theorem/accounting';
import {
  DepositRequestStatus,
  DepositStatus,
  type IDepositForfeitureRule,
  type IDepositMetadata,
  type ISchedulingEventReason,
} from '@principle-theorem/principle-core/interfaces';
import { isSameRef, type DocumentReference } from '@principle-theorem/shared';
import { type IDepositForfeitureContext } from './deposit-forfeiture-control';

export enum PlannedDepositOutcomeKind {
  None = 'none',
  Forfeit = 'forfeit',
  Charge = 'charge',
}

export interface IPlannedDepositOutcome {
  kind: PlannedDepositOutcomeKind;
  forfeitAmountCents?: number;
  depositAmountCents?: number;
  currency?: Currency;
}

export interface IPlannedDepositOutcomeControlState {
  context?: IDepositForfeitureContext;
  shouldForfeit: boolean;
  amountDollars?: number;
}

export interface IDepositForfeitureWarningInputs {
  reasonForfeiturePercentage?: number;
  outcome: IPlannedDepositOutcome;
  canOverride: boolean;
  /** Whether the appointment actually holds deposit money to act on. */
  depositAtStake: boolean;
  /**
   * Whether the forfeiture toggle is actually rendered and editable on this
   * screen, reported by the control itself. `depositAtStake` reads the
   * appointment's deposit metadata, which can say `Paid` for a staff-recorded
   * deposit that has no deposit request document behind it — the control then
   * never renders. Only this flag may gate copy that points at a control.
   */
  canActionHere: boolean;
}

const CTA_FORFEIT_SUFFIX = ' & Forfeit Deposit';
const CTA_CHARGE_SUFFIX = ' & Charge Deposit';
const FALLBACK_CURRENCY: Currency = Currency.AUD;

/**
 * One source of truth for what saving a cancellation or reschedule will do to
 * the deposit, so the CTA verb, the summary sentence, and the pages that show
 * them can never disagree about the planned outcome.
 *
 * The verbs mirror `SchedulingEventDepositForfeiture`: an Authorised
 * cancellation-cover hold is CAPTURED via Stripe (charge), while a Paid
 * deposit is kept by consuming its account credit against a cancellation-fee
 * invoice (forfeit).
 */
export class PlannedDepositOutcome {
  /**
   * Derives the planned outcome from the forfeiture control's live state,
   * including staff overrides of the toggle and amount. Mirrors the server's
   * no-op rule: a zero or missing amount forfeits nothing.
   */
  static fromControlState(
    state: IPlannedDepositOutcomeControlState
  ): IPlannedDepositOutcome {
    const context = state.context;
    if (!context || !state.shouldForfeit) {
      return { kind: PlannedDepositOutcomeKind.None };
    }
    const forfeitAmountCents = Money.toCents(state.amountDollars ?? 0);
    if (forfeitAmountCents <= 0) {
      return { kind: PlannedDepositOutcomeKind.None };
    }
    return {
      kind:
        context.depositRequest.status === DepositRequestStatus.Authorised
          ? PlannedDepositOutcomeKind.Charge
          : PlannedDepositOutcomeKind.Forfeit,
      forfeitAmountCents,
      depositAmountCents: context.depositRequest.amountCents,
      currency: context.depositRequest.currency,
    };
  }

  /**
   * Verb-only CTA suffix. Amounts deliberately never appear on the button;
   * they belong in {@link summary}.
   */
  static ctaSuffix(outcome: IPlannedDepositOutcome): string {
    switch (outcome.kind) {
      case PlannedDepositOutcomeKind.Charge:
        return CTA_CHARGE_SUFFIX;
      case PlannedDepositOutcomeKind.Forfeit:
        return CTA_FORFEIT_SUFFIX;
      case PlannedDepositOutcomeKind.None:
      default:
        return '';
    }
  }

  /**
   * The sentence shown beside the CTA. Remainder copy matches what the server
   * actually does: a partial capture releases the rest of the hold, and a
   * partial forfeiture leaves the rest of the deposit as patient credit.
   */
  static summary(outcome: IPlannedDepositOutcome): string | undefined {
    if (
      outcome.kind === PlannedDepositOutcomeKind.None ||
      outcome.forfeitAmountCents === undefined ||
      outcome.depositAmountCents === undefined
    ) {
      return;
    }
    const currency = outcome.currency ?? FALLBACK_CURRENCY;
    const depositAmount = Money.formatPlainCents(
      outcome.depositAmountCents,
      currency
    );
    const forfeitAmount = Money.formatPlainCents(
      outcome.forfeitAmountCents,
      currency
    );
    const isPartial = outcome.forfeitAmountCents < outcome.depositAmountCents;
    if (outcome.kind === PlannedDepositOutcomeKind.Charge) {
      return isPartial
        ? `${forfeitAmount} of the ${depositAmount} held on the patient's card will be charged as a cancellation fee; the rest of the hold is released.`
        : `The ${depositAmount} held on the patient's card will be charged as a cancellation fee.`;
    }
    return isPartial
      ? `${forfeitAmount} of the ${depositAmount} deposit will be forfeited as a cancellation fee; the remainder stays as patient credit.`
      : `The ${depositAmount} deposit will be forfeited as a cancellation fee.`;
  }

  /**
   * Whether the appointment carries deposit money a cancellation reason could
   * act on. Gates the reason tooltip's outcome lines so surfaces without a
   * live deposit render exactly as before.
   */
  static depositAtStake(metadata?: IDepositMetadata): boolean {
    return (
      metadata !== undefined &&
      [DepositStatus.Authorised, DepositStatus.Paid].includes(metadata.status)
    );
  }

  /**
   * The forfeiture percentage the settings configure for a reason, matched the
   * same way `resolveDepositForfeitureContext` matches it so the display can
   * never disagree with the rule the save path will apply.
   */
  static reasonForfeiturePercentage(
    rules?: IDepositForfeitureRule[],
    reasonRef?: DocumentReference<ISchedulingEventReason>
  ): number | undefined {
    if (!rules || !reasonRef) {
      return;
    }
    return rules.find((rule) => isSameRef(rule.reasonRef, reasonRef))
      ?.forfeiturePercentage;
  }

  /**
   * The card's single warning slot. Flags the gap between what the chosen
   * reason normally forfeits and what this scheduling decision will actually
   * do, so a deposit a rule expects to be kept is never silently released.
   *
   * Returns at most one string so the card never stacks two amber blocks that
   * say overlapping things. Priority runs from "you cannot action this here"
   * down to "you changed the amount", because an instruction to use a control
   * that is not on screen is worse than no instruction at all. That is why the
   * instructional copy is gated on `canActionHere` — what the control reports
   * about itself — and never on deposit metadata or permission alone.
   */
  static forfeitureWarning(
    inputs: IDepositForfeitureWarningInputs
  ): string | undefined {
    const percentage = inputs.reasonForfeiturePercentage;
    const forfeitsByRule = percentage !== undefined && percentage > 0;
    if (forfeitsByRule && !inputs.depositAtStake) {
      const portion = PlannedDepositOutcome._portion(percentage);
      return `This reason normally forfeits ${portion}, but no deposit has been taken for this appointment, so there is nothing to forfeit.`;
    }
    const forfeitsNothing =
      inputs.outcome.kind === PlannedDepositOutcomeKind.None;
    if (forfeitsByRule && forfeitsNothing) {
      return PlannedDepositOutcome._forfeitureOffWarning(percentage, inputs);
    }
    if (forfeitsNothing || percentage === undefined) {
      return;
    }
    return PlannedDepositOutcome._amountDeviationWarning(
      percentage,
      inputs.outcome
    );
  }

  /**
   * Names the configured share the way staff read it on the settings screen,
   * so "100%" is never shown where "the full deposit" is meant.
   */
  private static _portion(percentage: number): string {
    return percentage >= 100
      ? 'the full deposit'
      : `${percentage}% of the deposit`;
  }

  /**
   * The appointment holds deposit money and the rule wants it kept, but this
   * decision releases it. Only staff looking at a rendered, editable toggle
   * are told to use it; permission alone is not enough, because a deposit with
   * no deposit request behind it never renders a toggle to turn on.
   */
  private static _forfeitureOffWarning(
    percentage: number,
    inputs: IDepositForfeitureWarningInputs
  ): string {
    const portion = PlannedDepositOutcome._portion(percentage);
    if (inputs.canActionHere) {
      return `This reason normally forfeits ${portion}, but forfeiture is switched off for this cancellation. Turn on 'Forfeit deposit' above to charge it.`;
    }
    if (!inputs.canOverride) {
      return `This reason normally forfeits ${portion}, but forfeiture is switched off and you do not have permission to change it.`;
    }
    return `This reason normally forfeits ${portion}, but this deposit cannot be forfeited automatically. Review it on the patient's account.`;
  }

  /**
   * A forfeiture is planned, but for a different amount than the rule sets.
   * The default is rounded exactly as `resolveDepositForfeitureContext` rounds
   * it, so an amount left untouched never reads as a deviation.
   */
  private static _amountDeviationWarning(
    percentage: number,
    outcome: IPlannedDepositOutcome
  ): string | undefined {
    const { forfeitAmountCents, depositAmountCents } = outcome;
    if (forfeitAmountCents === undefined || depositAmountCents === undefined) {
      return;
    }
    const defaultAmountCents = Money.percentageOfCents(
      depositAmountCents,
      percentage
    );
    if (forfeitAmountCents === defaultAmountCents) {
      return;
    }
    const currency = outcome.currency ?? FALLBACK_CURRENCY;
    const forfeitAmount = Money.formatPlainCents(forfeitAmountCents, currency);
    const defaultAmount = Money.formatPlainCents(defaultAmountCents, currency);
    return `You are forfeiting ${forfeitAmount} rather than the ${defaultAmount} this reason normally forfeits.`;
  }
}
